import { useEffect, useState } from 'react'
import { Link, Outlet, useMatches } from 'react-router'
import { Logo } from '#app/components/ui/logo.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export default function MarketingLayout() {
	const user = useOptionalUser()
	const matches = useMatches()
	const isAuthPage = matches.some((match) => {
		const pathname = match.pathname
		return (
			pathname === '/login' ||
			pathname === '/signup' ||
			pathname.startsWith('/forgot-password') ||
			pathname.startsWith('/reset-password') ||
			pathname.startsWith('/verify') ||
			pathname.startsWith('/onboarding')
		)
	})

	return (
		<div className="clair flex min-h-screen flex-col justify-between">
			{!isAuthPage && <MarketingHeader authed={Boolean(user)} />}
			<div className="flex flex-1 flex-col">
				<Outlet />
			</div>
			{!isAuthPage && <MarketingFooter />}
		</div>
	)
}

function MarketingHeader({ authed }: { authed: boolean }) {
	const [scrolled, setScrolled] = useState(false)
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 24)
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [])

	return (
		<header
			className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
			style={{
				padding: scrolled ? '14px 0' : '22px 0',
				background: scrolled ? 'rgba(14,18,32,0.72)' : 'transparent',
				backdropFilter: scrolled ? 'blur(20px) saturate(140%)' : 'none',
				WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(140%)' : 'none',
				borderBottom: scrolled
					? '1px solid rgba(255,255,255,0.06)'
					: '1px solid transparent',
			}}
		>
			<div className="mx-auto flex max-w-[1240px] items-center justify-between px-5 md:px-10">
				<div className="flex items-center gap-10">
					<Logo size="sm" color="white" />

				</div>
				<div className="flex items-center gap-2.5">
					{authed ? (
						<Link
							to="/app"
							className="inline-flex h-10 items-center rounded-xl bg-brand px-4 text-sm font-medium text-white transition-all hover:bg-brand-hover"
							style={{
								boxShadow:
									'0 1px 0 rgba(255,255,255,0.15) inset, 0 8px 24px -8px var(--brand-glow)',
							}}
						>
							Tableau de bord
						</Link>
					) : (
						<>
							<Link
								to="/login"
								className="hidden text-sm font-medium text-white/70 transition-colors hover:text-white md:inline-block"
							>
								Se connecter
							</Link>
							<Link
								to="/signup"
								className="inline-flex h-10 items-center rounded-xl bg-brand px-4 text-sm font-medium text-white transition-all hover:bg-brand-hover"
								style={{
									boxShadow:
										'0 1px 0 rgba(255,255,255,0.15) inset, 0 8px 24px -8px var(--brand-glow)',
								}}
							>
								S'inscrire
							</Link>
						</>
					)}
				</div>
			</div>
		</header>
	)
}

function MarketingFooter() {
	const sections = [
		{
			title: 'Produit',
			links: [
				{ label: 'Fonctionnalités', href: '#fonctionnalites' },
				{ label: 'Tarifs', href: '#tarifs' },
				{ label: 'Démo', href: '#demo' },
			],
		},
		{
			title: 'Ressources',
			links: [
				{ label: 'Documentation', href: '#' },
				{ label: 'Guide de la veille', href: '#' },
				{ label: 'Support', href: '#' },
			],
		},
		{
			title: 'Entreprise',
			links: [
				{ label: 'À propos', href: '#' },
				{ label: 'Contact', href: '#' },
			],
		},
		{
			title: 'Légal',
			links: [
				{ label: 'Confidentialité', href: '/privacy' },
				{ label: 'Conditions', href: '/terms' },
				{ label: 'LCAP', href: '/lcap' },
			],
		},
	]
	return (
		<footer
			className="pt-18 pb-8"
			style={{
				background: 'var(--ink-footer)',
				color: 'rgba(255,255,255,0.6)',
				borderTop: '1px solid rgba(255,255,255,0.06)',
			}}
		>
			<div className="mx-auto max-w-[1240px] px-5 md:px-10">
				<div className="mb-16 grid gap-12 md:grid-cols-[1.4fr_repeat(4,1fr)]">
					<div>
						<Logo size="sm" color="white" />
						<p
							className="mt-5 max-w-[260px] text-sm leading-[1.6]"
							style={{ color: 'rgba(255,255,255,0.6)' }}
						>
							La veille médiatique québécoise, pensée pour les équipes
							qui n'ont pas le temps de tout lire.
						</p>
						<div
							className="mt-6 flex items-center gap-2 text-xs"
							style={{ color: 'rgba(255,255,255,0.45)' }}
						>
							<span
								className="size-1.5 rounded-full"
								style={{ background: 'var(--success, #1F9D68)' }}
							/>
							Fait à Montréal · Hébergé au Canada
						</div>
					</div>
					{sections.map((s) => (
						<div key={s.title}>
							<div
								className="mb-4 text-xs font-medium uppercase tracking-[0.1em]"
								style={{ color: 'rgba(255,255,255,0.4)' }}
							>
								{s.title}
							</div>
							<ul className="space-y-2.5">
								{s.links.map((l) =>
									l.href.startsWith('/') ? (
										<li key={l.label}>
											<Link
												to={l.href}
												className="text-sm transition-colors hover:text-white"
												style={{ color: 'rgba(255,255,255,0.7)' }}
											>
												{l.label}
											</Link>
										</li>
									) : (
										<li key={l.label}>
											<a
												href={l.href}
												className="text-sm transition-colors hover:text-white"
												style={{ color: 'rgba(255,255,255,0.7)' }}
											>
												{l.label}
											</a>
										</li>
									),
								)}
							</ul>
						</div>
					))}
				</div>
				<div
					className="flex flex-wrap items-center justify-between gap-4 border-t pt-7 text-xs"
					style={{
						borderColor: 'rgba(255,255,255,0.06)',
						color: 'rgba(255,255,255,0.4)',
					}}
				>
					<div>© {new Date().getFullYear()} Vigilio · Fait à Montréal</div>
					<div className="font-mono">bonjour@vigilio.ca</div>
				</div>
			</div>
		</footer>
	)
}
